import { useEffect, useMemo, useState } from 'react';
import { toSpecialFixed } from '@/lib/misc/utils';
import { OnFormSubmitProp } from '@shared/types/frontend/sendFunds';
import KeypadHeader from './KeypadHeader';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import Keypad from './Keypad';
import { CovalentBalanceItem } from '@shared/types/frontend';
const AssetPercentageToggle = ({
  onPercentageChange
}: {
  onPercentageChange: (percentage: number) => void;
}) => {
  const [selectedPercentage, setSelectedPercentage] = useState<string>('0');
  const handleToggle = (value: string) => {
    setSelectedPercentage(value);
    const percentage = value === 'All' ? 100 : parseInt(value);
    onPercentageChange(percentage);
  };
  return <div className='flex items-center justify-center mb-4' data-sentry-component="AssetPercentageToggle" data-sentry-source-file="KeypadController.tsx">
      <ToggleGroup type='single' value={selectedPercentage} onValueChange={handleToggle} className='gap-4' data-sentry-element="ToggleGroup" data-sentry-source-file="KeypadController.tsx">
        {['10', '25', '50', 'All'].map(value => <ToggleGroupItem key={value} value={value} disabled={value === selectedPercentage} className='bg-genius-blue text-genius-cream
             disabled:opacity-100 rounded-full h-[31px] w-[61px] text-[14px] data-[state=on]:bg-genius-blue'>
            {value === 'All' ? value : `${value}%`}
          </ToggleGroupItem>)}
      </ToggleGroup>
    </div>;
};
export default function KeypadController({
  tokenUserBalance,
  form
}: {
  tokenUserBalance: CovalentBalanceItem;
  form: OnFormSubmitProp['form'];
}) {
  const [isSwitched, setIsSwitched] = useState(false);
  const _watchTokenAmount = form.watch('tokenAmount');
  const _watchTokenUsdAmount = form.watch('tokenUsdAmount');
  const _isMaxBalanceInputed = useMemo(() => {
    if (!tokenUserBalance) return false;
    const [_maxTokenBalance, _maxUsdBalance] = [parseFloat(_watchTokenAmount) >= tokenUserBalance.formatted_balance, parseFloat(_watchTokenUsdAmount) >= tokenUserBalance.usd_value];
    if (_watchTokenAmount === '0' || _watchTokenUsdAmount === '0') return false;
    if (_maxTokenBalance || _maxUsdBalance) {
      return true;
    }
    return false;
  }, [tokenUserBalance, _watchTokenAmount, _watchTokenUsdAmount]);
  useEffect(() => {
    if (_isMaxBalanceInputed) {
      form.setValue('tokenAmount', toSpecialFixed(tokenUserBalance.formatted_balance, 3, false, false).value);
      form.setValue('tokenUsdAmount', toSpecialFixed(tokenUserBalance.usd_value, 3, false, false).value);
      return;
    }
    return () => {};
  }, [_isMaxBalanceInputed]);
  function handleAssetPercentage(percentage: number) {
    const amountToken = Number(tokenUserBalance?.formatted_balance * (percentage / 100));
    console.log('SendFunds: getQuoteAndTransferParams percentage amount in token', toSpecialFixed(amountToken, 3, false, false).value);
    form.setValue('tokenAmount', toSpecialFixed(amountToken, 3, false, false).value);
    form.setValue('tokenUsdAmount', toSpecialFixed(amountToken * tokenUserBalance.quote_rate, 3, false, false).value);
  }
  const handleNumberInput = (val: string) => {
    let _returnedValue = val;
    if (!val || isNaN(parseFloat(val))) {
      _returnedValue = '0';
    }
    if (val?.includes('.')) {
      let parts = val.split('.');
      let integerPart = parts[0];
      let decimalPart = parts[1];
      let decimalLength = decimalPart.length;
      if (decimalLength > 4) {
        decimalPart = decimalPart.substring(0, 4);
      }
      _returnedValue = `${integerPart}.${decimalPart}`;
    }
    return _returnedValue;
  };
  const handleNumPadUsdChange = (val: string) => {
    const tokenUsdAmount = handleNumberInput(val);
    const _usdToToken = toSpecialFixed(parseFloat(val) / tokenUserBalance.quote_rate).value;
    form.setValue('tokenUsdAmount', tokenUsdAmount);
    form.setValue('tokenAmount', _usdToToken);
  };
  const handleNumPadTokenChange = (val: string) => {
    const tokenAmount = handleNumberInput(val);
    const _tokenToUsd = toSpecialFixed(parseFloat(val) * tokenUserBalance.quote_rate).value;
    console.log('handleNumPadTokenChange', {
      val,
      tokenAmount,
      _tokenToUsd
    });
    form.setValue('tokenAmount', tokenAmount);
    form.setValue('tokenUsdAmount', isNaN(Number(_tokenToUsd)) ? '0' : _tokenToUsd);
  };
  return <div className='bg-genius-indigo relative w-full ' data-sentry-component="KeypadController" data-sentry-source-file="KeypadController.tsx">
      <div className='z-10 flex flex-col items-center w-full pt-4'>
        <KeypadHeader tokenAmount={_watchTokenAmount} token={tokenUserBalance} usdAmount={_watchTokenUsdAmount} isSwitched={isSwitched} setIsSwitched={setIsSwitched} data-sentry-element="KeypadHeader" data-sentry-source-file="KeypadController.tsx" />
      </div>
      {/* percentage buttons */}
      <AssetPercentageToggle onPercentageChange={handleAssetPercentage} data-sentry-element="AssetPercentageToggle" data-sentry-source-file="KeypadController.tsx" />
      <div className='w-full flex flex-col text-2xl gap-10 pt-4 items-center justify-center keypad'>
        <Keypad handleNumPadTokenChange={handleNumPadTokenChange} isSwitched={isSwitched} usdValue={_watchTokenUsdAmount} tokenAmount={_watchTokenAmount} handleNumPadUsdChange={handleNumPadUsdChange} disablePads={_isMaxBalanceInputed} data-sentry-element="Keypad" data-sentry-source-file="KeypadController.tsx" />
      </div>
    </div>;
}